class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.detailsEl = this.querySelector('details');
    this.summaryEl = this.detailsEl?.querySelector('summary');
    this.contentEl = this.summaryEl?.nextElementSibling;
    this.bodyEl = null;
    this.animations = null;
    
    this.isIpad = matchMedia('(min-width: 1024px) and (hover: none)').matches;
    
    this.handleToggle = this.handleToggle.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
  }

  connectedCallback() {
    this.bodyEl = document.body;
    
    if (this.isIpad) {
      this.querySelector('a')?.classList.add('tw-pointer-events-none');
    }
    
    this.detailsEl?.addEventListener('toggle', this.handleToggle);
    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
  }

  disconnectedCallback() {
    this.detailsEl?.removeEventListener('toggle', this.handleToggle);
    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
  }

  get scrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }

  setBodyOverflow(lock) {
    if (lock) {
      const width = this.scrollbarWidth;
      if (width > 0) {
        this.bodyEl.style.paddingRight = `${width}px`;
      }
      this.bodyEl.classList.add('overflow-hidden');
    } else {
      this.bodyEl.classList.remove('overflow-hidden');
      this.bodyEl.style.paddingRight = '';
    }
  }

  setBlur(active) {
    this.bodyEl?.classList.toggle('tw-custom-blur', active);
    this.setBodyOverflow(active);
  }

  open() {
    this.detailsEl.open = true;
  }

  close() {
    this.detailsEl.open = false;
    this.summaryEl?.setAttribute('aria-expanded', 'false');
    this.setBlur(false);
  }

  handleToggle() {
    this.animations ??= this.contentEl?.getAnimations() ?? [];
    
    if (this.detailsEl.open) {
      this.animations.forEach(a => a.play());
    } else {
      this.animations.forEach(a => a.cancel());
    }
  }

  handleMouseEnter() {
    this.open();
    this.setBlur(true);
  }

  handleMouseLeave() {
    this.close();
  }

  handleFocusIn() {
    this.open();
    this.setBlur(true);
  }

  handleFocusOut() {
    requestAnimationFrame(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }
}


class HeaderMenu extends DetailsDisclosure {
  /** @type {HeaderMenu|null} Currently open instance (shared across all menus) */
  static _activeInstance = null;

  constructor() {
    super();
    this.headerEl = null;
    this._closeTimer = null;
    this._closeFallback = null;
    this._onCloseEnd = null;
    this._isClosing = false;

    this.handleMouseEnter = this._onMouseEnter.bind(this);
    this.handleMouseLeave = this._onMouseLeave.bind(this);
    this.handleFocusIn = this._onFocusIn.bind(this);
    this.handleFocusOut = this._onFocusOut.bind(this);
    this._handleContentOver = this._onContentOver.bind(this);
    this._handleContentOut = this._onContentOut.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    this.headerEl = document.querySelector('.header-wrapper');

    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);

    this.summaryEl?.addEventListener('mouseenter', this.handleMouseEnter);
    this.summaryEl?.addEventListener('mouseleave', this.handleMouseLeave);

    this.contentEl?.addEventListener('mouseover', this._handleContentOver);
    this.contentEl?.addEventListener('mouseout', this._handleContentOut);
  }

  disconnectedCallback() {
    clearTimeout(this._closeTimer);
    clearTimeout(this._closeFallback);
    if (HeaderMenu._activeInstance === this) {
      HeaderMenu._activeInstance = null;
    }
    this.summaryEl?.removeEventListener('mouseenter', this.handleMouseEnter);
    this.summaryEl?.removeEventListener('mouseleave', this.handleMouseLeave);
    this.contentEl?.removeEventListener('mouseover', this._handleContentOver);
    this.contentEl?.removeEventListener('mouseout', this._handleContentOut);
    super.disconnectedCallback();
  }

  /**
   * Removes any pending transitionend listener from a previous close().
   * @private
   */
  _dropCloseListener() {
    if (this._onCloseEnd) {
      this.contentEl?.removeEventListener('transitionend', this._onCloseEnd);
      this._onCloseEnd = null;
    }
  }

  /**
   * Toggles the body overlay and scroll lock for the mega-menu.
   * @param {boolean} active
   * @private
   */
  _setOverlay(active) {
    if (active) {
      const width = this.scrollbarWidth;
      if (width > 0) {
        this.bodyEl.style.paddingRight = `${width}px`;
      }
      this.bodyEl.classList.add('overflow-hidden', 'has-mega-menu-open');
    } else {
      this.bodyEl.classList.remove('overflow-hidden', 'has-mega-menu-open');
      this.bodyEl.style.paddingRight = '';
    }
  }

  /**
   * Opens the menu — CSS transitions handle the visual reveal.
   * @override
   */
  open() {
    clearTimeout(this._closeFallback);
    this._dropCloseListener();
    this._isClosing = false;
    this.detailsEl.open = true;
    void this.contentEl?.offsetHeight;
    this.classList.add('is-open');
  }

  /**
   * Closes the menu — waits for CSS transition before removing [open].
   * @override
   */
  close() {
    clearTimeout(this._closeTimer);
    clearTimeout(this._closeFallback);
    this._dropCloseListener();

    if (!this.classList.contains('is-open')) {
      this.detailsEl.open = false;
      this.summaryEl?.setAttribute('aria-expanded', 'false');
      if (HeaderMenu._activeInstance === this) HeaderMenu._activeInstance = null;
      if (!HeaderMenu._activeInstance) this._setOverlay(false);
      return;
    }

    this._isClosing = true;
    this.classList.remove('is-open');

    const finishClose = () => {
      if (!this._isClosing) return;
      this._isClosing = false;
      clearTimeout(this._closeFallback);
      this._dropCloseListener();
      this.detailsEl.open = false;
      this.summaryEl?.setAttribute('aria-expanded', 'false');
    };

    this._onCloseEnd = (e) => {
      if (e.target !== this.contentEl) return;
      finishClose();
    };

    this.contentEl?.addEventListener('transitionend', this._onCloseEnd);
    this._closeFallback = setTimeout(() => finishClose(), 500);

    if (HeaderMenu._activeInstance === this) {
      HeaderMenu._activeInstance = null;
    }
    if (!HeaderMenu._activeInstance) {
      this._setOverlay(false);
    }
  }

  /**
   * Instantly reveals this menu with no transition — the incoming item when
   * switching directly between menu items. The transition is restored on the
   * next frames so the eventual close still animates.
   * @private
   */
  _instantOpen() {
    clearTimeout(this._closeFallback);
    this._dropCloseListener();
    this._isClosing = false;
    this.classList.add('is-instant');
    this.detailsEl.open = true;
    void this.contentEl?.offsetHeight;
    this.classList.add('is-open');
    this._clearInstant();
  }

  /**
   * Instantly hides this menu with no transition — the outgoing item when
   * switching directly between menu items.
   * @private
   */
  _instantClose() {
    clearTimeout(this._closeTimer);
    clearTimeout(this._closeFallback);
    this._dropCloseListener();
    this._isClosing = false;
    this.classList.add('is-instant');
    this.classList.remove('is-open');
    this.detailsEl.open = false;
    this.summaryEl?.setAttribute('aria-expanded', 'false');
    this._clearInstant();
  }

  /**
   * Drops the no-transition flag after two frames so later open/close animate.
   * @private
   */
  _clearInstant() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.classList.remove('is-instant'));
    });
  }

  /** @private */
  _scheduleClose() {
    clearTimeout(this._closeTimer);
    this._closeTimer = setTimeout(() => this.close(), 150);
  }

  /** @private */
  _onMouseEnter() {
    clearTimeout(this._closeTimer);
    clearTimeout(this._closeFallback);

    const prev = HeaderMenu._activeInstance;
    HeaderMenu._activeInstance = this;
    this._setOverlay(true);

    if (prev && prev !== this) {
      // Switching straight to another item swaps with no animation.
      prev._instantClose();
      this._instantOpen();
    } else {
      this.open();
    }
  }

  /** @private */
  _onMouseLeave() {
    this._scheduleClose();
  }

  /** @private */
  _onFocusIn() {
    clearTimeout(this._closeTimer);
    clearTimeout(this._closeFallback);

    const prev = HeaderMenu._activeInstance;
    HeaderMenu._activeInstance = this;
    this._setOverlay(true);

    if (prev && prev !== this) {
      // Switching straight to another item swaps with no animation.
      prev._instantClose();
      this._instantOpen();
    } else {
      this.open();
    }
  }

  /** @private */
  _onFocusOut() {
    requestAnimationFrame(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  /**
   * Cancels a pending close when the cursor reaches a pointer-events-auto
   * child inside the content wrapper (mouseover bubbles through
   * pointer-events: none parents, unlike mouseenter).
   * @private
   */
  _onContentOver() {
    clearTimeout(this._closeTimer);
  }

  /**
   * Schedules a close when the cursor leaves the content area, unless
   * it moved to another element still inside this header-menu.
   * @param {MouseEvent} e
   * @private
   */
  _onContentOut(e) {
    if (e.relatedTarget && this.contains(e.relatedTarget)) return;
    this._scheduleClose();
  }

  /** @override */
  handleToggle() {
    if (this.detailsEl?.open) {
      const content = this.querySelector('.mega-menu__content');
      if (content) content.scrollTop = 0;
    }

    if (!this.headerEl) return;

    this.headerEl.preventHide = !!HeaderMenu._activeInstance;

    const prop = '--header-bottom-position-desktop';
    if (!document.documentElement.style.getPropertyValue(prop)) {
      const bottom = Math.floor(this.headerEl.getBoundingClientRect().bottom);
      document.documentElement.style.setProperty(prop, `${bottom}px`);
    }
  }
}


customElements.define('details-disclosure', DetailsDisclosure);
customElements.define('header-menu', HeaderMenu);
 